import { useState, useEffect, useCallback } from "react";
import { History, RefreshCw, CreditCard, RotateCcw, AlertCircle, CheckCircle2, Clock, XCircle, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { 
      variant: "outline", 
      className: "bg-gray-100 text-gray-800 border-gray-200",
      icon: Clock
    },
    in_progress: { 
      variant: "outline", 
      className: "bg-yellow-100 text-yellow-800 border-yellow-200 status-pulse",
      icon: Clock
    },
    completed: { 
      variant: "outline", 
      className: "bg-green-100 text-green-800 border-green-200",
      icon: CheckCircle2
    },
    captured: { 
      variant: "outline", 
      className: "bg-green-100 text-green-800 border-green-200",
      icon: CheckCircle2
    },
    refunded: { 
      variant: "outline", 
      className: "bg-blue-100 text-blue-800 border-blue-200",
      icon: RotateCcw
    },
    failed: { 
      variant: "outline", 
      className: "bg-red-100 text-red-800 border-red-200",
      icon: XCircle
    },
    declined: { 
      variant: "outline", 
      className: "bg-red-100 text-red-800 border-red-200",
      icon: XCircle
    },
    pending_action: { 
      variant: "outline", 
      className: "bg-orange-100 text-orange-800 border-orange-200",
      icon: AlertCircle
    }
  };

  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <Badge variant={config.variant} className={`${config.className} flex items-center gap-1 text-xs`}>
      <Icon className="w-3 h-3" />
      <span className="hidden sm:inline">{status.replace("_", " ")}</span>
      <span className="sm:hidden">{status.replace("_", " ").slice(0, 4)}</span>
    </Badge>
  );
};

const TypeBadge = ({ type }) => {
  const config = {
    payment: {
      icon: CreditCard,
      className: "bg-blue-50 text-blue-700 border-blue-200"
    },
    refund: {
      icon: RotateCcw,
      className: "bg-orange-50 text-orange-700 border-orange-200"
    },
    moto: {
      icon: Phone,
      className: "bg-purple-50 text-purple-700 border-purple-200"
    }
  };
  
  const { icon: Icon, className } = config[type] || config.payment;
  
  return (
    <Badge 
      variant="outline" 
      className={`flex items-center gap-1 text-xs ${className}`}
    >
      <Icon className="w-3 h-3" />
      <span className="hidden sm:inline">{type === "moto" ? "MOTO" : type}</span>
    </Badge>
  );
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit"
  }).format(date);
};

const formatDateMobile = (dateString) => {
  if (!dateString) return "-";
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short"
  }).format(date);
};

export default function HistoryPage() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Set page title
  useEffect(() => {
    document.title = "History | GeekTickets POS";
  }, []);

  const fetchTransactions = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    
    try {
      const response = await axios.get(`${API}/transactions`);
      setTransactions(response.data);
    } catch (error) {
      console.error("Failed to fetch transactions:", error);
      toast.error("Failed to load transactions");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchTransactions();
    
    // Auto-refresh every 10 seconds to catch webhook updates
    const interval = setInterval(() => {
      fetchTransactions(false);
    }, 10000);
    
    return () => clearInterval(interval);
  }, [fetchTransactions]);

  const handleRefresh = () => {
    fetchTransactions(true);
    toast.success("Transactions refreshed");
  };

  const completedPayments = transactions.filter(t => 
    (t.transaction_type === "payment" || t.transaction_type === "moto") && 
    (t.status === "completed" || t.status === "captured")
  );
  const totalProcessed = completedPayments.reduce((sum, t) => sum + (t.amount || 0), 0);

  return (
    <div className="min-h-screen p-4 sm:p-6 md:p-8 lg:p-12">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="space-y-1">
            <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-2 sm:gap-3">
              <History className="w-6 h-6 sm:w-8 sm:h-8" />
              History
            </h1>
            <p className="text-sm text-muted-foreground">
              All payments and refunds
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-button"
            className="w-full sm:w-auto"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>

        {/* Stats Cards - Mobile optimized grid */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
          <Card>
            <CardContent className="pt-4 sm:pt-6 px-3 sm:px-6">
              <div className="text-xl sm:text-2xl font-bold font-mono">
                {transactions.length}
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground">Total</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 sm:pt-6 px-3 sm:px-6">
              <div className="text-xl sm:text-2xl font-bold font-mono text-blue-600">
                {transactions.filter(t => t.transaction_type === "payment").length}
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground">In-Person</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 sm:pt-6 px-3 sm:px-6">
              <div className="text-xl sm:text-2xl font-bold font-mono text-purple-600">
                {transactions.filter(t => t.transaction_type === "moto").length}
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground">MOTO</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 sm:pt-6 px-3 sm:px-6">
              <div className="text-xl sm:text-2xl font-bold font-mono text-orange-600">
                {transactions.filter(t => t.transaction_type === "refund").length}
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground">Refunds</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 sm:pt-6 px-3 sm:px-6">
              <div className="text-xl sm:text-2xl font-bold font-mono text-green-600">
                €{totalProcessed.toFixed(2)}
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground">Processed</p>
            </CardContent>
          </Card>
        </div>

        {/* Transactions Table/List */}
        <Card>
          <CardHeader className="px-4 sm:px-6">
            <CardTitle className="text-base sm:text-lg">Recent Transactions</CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              Last 50 transactions (auto-refreshes)
            </CardDescription>
          </CardHeader>
          <CardContent className="px-2 sm:px-6">
            {loading ? (
              <div className="space-y-4 px-2">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center space-x-4">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="space-y-2 flex-1">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : transactions.length === 0 ? (
              <div className="text-center py-8 sm:py-12 px-4">
                <AlertCircle className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-medium text-base sm:text-lg">No transactions yet</h3>
                <p className="text-sm text-muted-foreground">
                  Transactions will appear here once processed
                </p>
              </div>
            ) : (
              <>
                {/* Desktop Table */}
                <div className="hidden md:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Order/Note</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-right">Fee</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transactions.map((tx) => (
                        <TableRow key={tx.id} data-testid={`transaction-row-${tx.id}`}>
                          <TableCell className="font-medium whitespace-nowrap text-sm">
                            {formatDate(tx.created_at)}
                          </TableCell>
                          <TableCell>
                            <TypeBadge type={tx.transaction_type} />
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground max-w-[180px]">
                            {tx.transaction_type === "moto" ? (
                              <div>
                                <div className="font-medium text-foreground">{tx.order_number}</div>
                                {tx.card_last5 && <div className="text-xs">****{tx.card_last5}</div>}
                              </div>
                            ) : (
                              <span className="truncate block">{tx.note || "-"}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm">
                            <span className={tx.transaction_type === "refund" ? "text-orange-600" : ""}>
                              {tx.transaction_type === "refund" ? "-" : ""}€{tx.amount?.toFixed(2) || "0.00"}
                            </span>
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm text-muted-foreground">
                            €{tx.platform_fee?.toFixed(2) || "0.00"}
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={tx.status} />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* Mobile List */}
                <div className="md:hidden space-y-3 px-2">
                  {transactions.map((tx) => (
                    <div 
                      key={tx.id} 
                      className="bg-muted/30 rounded-lg p-3 space-y-2"
                      data-testid={`transaction-card-${tx.id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <TypeBadge type={tx.transaction_type} />
                          <StatusBadge status={tx.status} />
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {formatDateMobile(tx.created_at)}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className={`font-mono text-lg font-bold ${
                          tx.transaction_type === "refund" ? "text-orange-600" : ""
                        }`}>
                          {tx.transaction_type === "refund" ? "-" : ""}€{tx.amount?.toFixed(2) || "0.00"}
                        </span>
                        {tx.platform_fee > 0 && (
                          <span className="text-xs text-muted-foreground font-mono">
                            Fee: €{tx.platform_fee?.toFixed(2)}
                          </span>
                        )}
                      </div>
                      {tx.transaction_type === "moto" ? (
                        <div className="text-xs text-muted-foreground">
                          <span className="font-medium">Order:</span> {tx.order_number}
                          {tx.card_last5 && <span className="ml-2">• ****{tx.card_last5}</span>}
                        </div>
                      ) : tx.note && (
                        <p className="text-xs text-muted-foreground italic truncate">
                          {tx.note}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
